import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  Notice,
  Section,
  badge,
  errorText,
  formatDate,
  input,
  mono,
  primary,
} from './PipelineSettingsPanel';
import { confirmDialog } from '../../components/ConfirmDialog';
import { pipelineApi } from '../../services/api';

// Meetings pipeline → Admission. The one application-wide bar a scored risk
// must clear to reach the live register (stage 4b), and how many may per
// meeting. Every scored concern keeps the version it was judged under.

const fail = (error) => toast.error(errorText(error));

const FIELDS = [
  {
    key: 'threshold',
    label: 'Score threshold',
    hint: 'Combined risk score (0–4) a concern needs to be admitted.',
    step: 0.1,
    min: 0,
    max: 4,
  },
  {
    key: 'quota_cutoff',
    label: 'Per-meeting quota',
    hint: 'Most risks admitted from one meeting. A ceiling, never padded.',
    step: 1,
    min: 0,
  },
  {
    key: 'corroboration',
    label: 'Corroborations to promote',
    hint: 'Meetings that must raise a hunch before it is watched actively.',
    step: 1,
    min: 1,
  },
];

const SOURCE = {
  defaults: ['shipped defaults', 'bg-zinc-800 text-zinc-300'],
  legacy: ['from a project policy', 'bg-amber-500/10 text-amber-300'],
  published: ['published', 'bg-emerald-500/10 text-emerald-300'],
};

const valuesOf = (data) => {
  const p = data.policy;
  return p
    ? {
        threshold: p.threshold,
        quota_cutoff: p.quota_cutoff,
        corroboration: p.promotion_trigger_default.threshold,
      }
    : { ...data.defaults };
};

export default function AdmissionPanel({ token, canManage }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [form, setForm] = useState({});
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [current, past] = await Promise.all([
      pipelineApi.admission(token),
      pipelineApi.admissionHistory(token),
    ]);
    setData(current);
    setHistory(past);
    setForm(valuesOf(current));
  }, [token]);

  useEffect(() => {
    load().catch(fail);
  }, [load]);

  if (!data || !history)
    return <p className="text-sm text-zinc-400">Loading…</p>;

  const live = valuesOf(data);
  const dirty = FIELDS.some((f) => Number(form[f.key]) !== Number(live[f.key]));
  const [sourceLabel, sourceTone] = SOURCE[data.source];

  const publish = async () => {
    const ok = await confirmDialog({
      title: 'Publish a new admission policy?',
      message:
        'Applies to every project from the next meeting scored. Concerns already committed keep the policy they were judged under.',
      confirmLabel: 'Publish',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await pipelineApi.publishAdmission(token, {
        threshold: Number(form.threshold),
        quota_cutoff: Number.parseInt(form.quota_cutoff, 10),
        promotion_trigger_default: {
          threshold: Number.parseInt(form.corroboration, 10),
        },
        change_rationale: rationale,
        // The server stamps the signed-in admin over this.
        changed_by_person_id: 'me',
      });
      setRationale('');
      await load();
      toast.success('Published');
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Section
        title="Risk admission"
        hint="Which scored risks reach the live register, for every project."
        action={
          <span className={`${badge} ${sourceTone}`}>
            {data.policy ? `${data.policy.version} · ` : ''}
            {sourceLabel}
          </span>
        }
      >
        {data.source === 'legacy' && (
          <div className="mb-4">
            <Notice>
              No application-wide policy is published yet, so the newest
              per-project policy ({data.policy.project_id}) applies everywhere.
              Publish one to replace it.
            </Notice>
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="text-sm font-medium text-zinc-200">
                {f.label}
              </span>
              <input
                aria-label={f.label}
                className={`${input} mt-1 tabular-nums`}
                type="number"
                step={f.step}
                min={f.min}
                max={f.max}
                value={form[f.key] ?? ''}
                disabled={!canManage}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              />
              <span className="mt-1 block text-xs text-zinc-400">
                {f.hint} Default {data.defaults[f.key]}.
              </span>
            </label>
          ))}
        </div>
        {canManage && dirty && (
          <div className="mt-4 space-y-2">
            <input
              className={input}
              placeholder="Why are you changing this? (required, kept in history)"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
            />
            <button
              className={primary}
              disabled={busy || !rationale.trim()}
              onClick={publish}
            >
              Publish new version
            </button>
          </div>
        )}
      </Section>

      <Section title="History" hint="Application-wide versions, newest first.">
        {history.policies.length === 0 ? (
          <p className="text-sm text-zinc-400">Nothing published yet.</p>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {history.policies.map((p) => (
              <li key={p.policy_id} className="py-2 text-sm">
                <span className={`${mono} text-zinc-200`}>{p.version}</span>{' '}
                <span className="tabular-nums text-zinc-400">
                  threshold {p.threshold} · quota {p.quota_cutoff} ·{' '}
                  {formatDate(p.created_at)} · {p.changed_by_person_id}
                </span>
                <div className="text-zinc-300">{p.change_rationale}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {history.legacy.length > 0 && (
        <Section
          title="Old per-project policies"
          hint="Kept for their history. Not applied once an application-wide version is published."
        >
          <ul className="divide-y divide-zinc-800">
            {history.legacy.map((p) => (
              <li key={p.policy_id} className="py-2 text-sm">
                <span className={`${mono} text-zinc-200`}>{p.policy_id}</span>{' '}
                <span className="tabular-nums text-zinc-400">
                  threshold {p.threshold} · quota {p.quota_cutoff} ·{' '}
                  {formatDate(p.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
